import type { LayerDef, Legend, LegendContext } from "../types";

/**
 * Natural hazards, as two layers with very different resolutions.
 *
 * **Clay shrink-swell** is real geology: BRGM polygons in three exposure
 * classes, finer than a commune. It is the largest single cause of structural
 * damage claims on French houses, and in the medium and high classes it forces
 * a geotechnical study on a land sale.
 *
 * **CatNat declarations** are a commune choropleth of what has actually been
 * declared since 1982. Every peril ships as its own attribute on the same
 * feature — `cn_inondation`, `cn_secheresse` and so on, plus a `cn20_` set
 * counting only the last twenty years — so switching which peril the map shades
 * is a paint expression over geometry the browser already holds.
 *
 * The two are deliberately not combined into one score. A commune that floods
 * and a commune built on shrinking clay have nothing in common except the word
 * "risk", and averaging them would hide which one you are buying into.
 */

/** BRGM's three exposure classes. Nothing drawn means no clay hazard mapped. */
export const CLAY_CLASSES: { value: number; color: string; label: string }[] = [
  { value: 1, color: "#fde9b8", label: "Low" },
  { value: 2, color: "#e8a33d", label: "Medium — study required on a land sale" },
  { value: 3, color: "#b3401f", label: "High — study required on a land sale" },
];

export const clayLayer: LayerDef = {
  id: "clay",
  label: "Clay shrink-swell",
  group: "risks",
  description:
    "Ground that swells and shrinks with the seasons, cracking foundations. The largest single cause of structural damage claims on French houses.",
  attribution:
    '<a href="https://www.georisques.gouv.fr/donnees/bases-de-donnees/retrait-gonflement-des-argiles">Retrait-gonflement des argiles · BRGM / Géorisques</a>',
  defaultVisible: false,
  legend: {
    unit: "clay exposure",
    stops: CLAY_CLASSES.map((c) => ({ color: c.color, label: c.label })),
    note: "Unshaded ground has no clay hazard mapped — that is an answer, not a gap.",
  },
  sources: {
    argiles: { type: "vector", url: "pmtiles:///tiles/argiles.pmtiles" },
  },
  layers: [
    {
      id: "clay-fill",
      type: "fill",
      source: "argiles",
      "source-layer": "argiles",
      minzoom: 8,
      paint: {
        "fill-color": [
          "match",
          ["get", "NIVEAU"],
          ...CLAY_CLASSES.flatMap((c) => [c.value, c.color]),
          "rgba(0,0,0,0)",
        ] as never,
        "fill-opacity": [
          "interpolate", ["linear"], ["zoom"], 8, 0.45, 13, 0.6,
        ] as never,
      },
    },
  ],
};

/**
 * Perils a buyer distinguishes between, each with the tile attribute holding
 * its count and the ramp that reads it.
 */
export type RiskMetricId =
  | "inondation"
  | "secheresse"
  | "mouvement"
  | "total";

interface RiskMetric {
  label: string;
  /** Tile attribute, given the window in force. */
  attr: (recentOnly: boolean) => string;
  stops: { value: number; color: string }[];
  note: string;
}

/**
 * Sequential rather than diverging: zero declarations is one end of the scale,
 * not a midpoint. Orange-to-brown so the layer cannot be read as the green
 * index or as the blue-red population map.
 */
const COUNT_STOPS = [
  { value: 0, color: "#f6f4f0" },
  { value: 2, color: "#f6ddb0" },
  { value: 5, color: "#eab167" },
  { value: 10, color: "#cf7a3c" },
  { value: 16, color: "#8f3b1b" },
];

const RECENT_STOPS = [
  { value: 0, color: "#f6f4f0" },
  { value: 1, color: "#f6ddb0" },
  { value: 3, color: "#eab167" },
  { value: 6, color: "#cf7a3c" },
  { value: 10, color: "#8f3b1b" },
];

export const RISK_METRICS: Record<RiskMetricId, RiskMetric> = {
  inondation: {
    label: "Flooding",
    attr: (r) => (r ? "cn20_inondation" : "cn_inondation"),
    stops: COUNT_STOPS,
    note: "Includes mudflow and groundwater rise, which are declared under the same heading.",
  },
  secheresse: {
    label: "Drought",
    attr: (r) => (r ? "cn20_secheresse" : "cn_secheresse"),
    stops: COUNT_STOPS,
    note: "Drought declarations track the clay layer closely — the damage is subsidence, not thirst.",
  },
  mouvement: {
    label: "Ground movement",
    attr: (r) => (r ? "cn20_mouvement" : "cn_mouvement"),
    stops: COUNT_STOPS,
    note: "Landslides, collapse and subsidence, including the old gypsum quarries.",
  },
  total: {
    label: "All perils",
    attr: (r) => (r ? "cn20_total" : "cn_total"),
    stops: COUNT_STOPS,
    note: "Every declared peril together. Useful for a first look, blunt for a decision.",
  },
};

export function catnatColor(metric: RiskMetricId, recentOnly: boolean): unknown[] {
  const def = RISK_METRICS[metric];
  const stops = recentOnly ? RECENT_STOPS : def.stops;
  const value = ["get", def.attr(recentOnly)];
  return [
    "case",
    ["==", value, null],
    "rgba(0,0,0,0)",
    ["interpolate", ["linear"], value, ...stops.flatMap((s) => [s.value, s.color])],
  ];
}

export function catnatLegend(metric: RiskMetricId, recentOnly: boolean): Legend {
  const def = RISK_METRICS[metric];
  const stops = recentOnly ? RECENT_STOPS : def.stops;
  return {
    unit: `${def.label}, declarations ${recentOnly ? "since 2006" : "since 1982"}`,
    stops: stops.map((s, i) => {
      const next = stops[i + 1];
      return {
        color: s.color,
        label: next ? `${s.value} – ${next.value}` : `${s.value}+`,
      };
    }),
    note: def.note,
  };
}

export const CATNAT_FILL = "catnat-fill";

const DEFAULT_METRIC: RiskMetricId = "inondation";

export const catnatLayer: LayerDef = {
  id: "catnat",
  label: "Disaster declarations",
  group: "risks",
  description:
    "Arrêtés de catastrophe naturelle per commune since 1982 — what has actually been declared here, rather than what is modelled.",
  attribution:
    '<a href="https://www.georisques.gouv.fr/donnees/bases-de-donnees/base-gaspar">GASPAR · Ministère de la Transition écologique</a>',
  defaultVisible: false,
  legend: catnatLegend(DEFAULT_METRIC, false),
  legendFor: (ctx: LegendContext) =>
    catnatLegend(ctx.riskMetric as RiskMetricId, ctx.riskRecentOnly),
  sources: {
    risk_commune: { type: "vector", url: "pmtiles:///tiles/risk_commune.pmtiles" },
  },
  layers: [
    {
      id: CATNAT_FILL,
      type: "fill",
      source: "risk_commune",
      "source-layer": "risk_commune",
      paint: {
        // Same fade as the fire layer, for the same reason: a per-commune count
        // is a regional comparison, and holding it opaque at street zoom hides
        // the streets.
        "fill-color": catnatColor(DEFAULT_METRIC, false) as never,
        "fill-opacity": [
          "interpolate", ["linear"], ["zoom"], 8, 0.62, 12, 0.42, 15, 0.16,
        ] as never,
      },
    },
    {
      id: "catnat-line",
      type: "line",
      source: "risk_commune",
      "source-layer": "risk_commune",
      paint: { "line-color": "#ffffff", "line-width": 0.6, "line-opacity": 0.7 },
    },
  ],
};
